import { CoreQuery, type ITransactionChargeRates } from "@gen/app-query";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Fragment, type ReactNode, useState } from "react";

import { PublicSvgIcon } from "~/components/public_svg_icon";
import { useConfig } from "~/contexts/config";
import { isAdminTransaction } from "~/entities/transaction";

interface ExtrasSectionProps {
  transactionData: Parameters<typeof isAdminTransaction>[0] & {
    charge_rates?: Omit<ITransactionChargeRates, "transaction_id">;
  };
  metadata?: unknown;
  transactionId?: number;
}

// Full dotted paths to hide from the Raw Transaction display.
const EXCLUDED_RAW_FIELD_PATHS = new Set([
  "funds_release.payout_summary.stripe_payout_id",
]);

function renderKeyValuePairs(
  data: unknown,
  localization: { locale: string; timeZone: string },
  testIdPrefix: string,
  prefix = "",
): ReactNode {
  if (Array.isArray(data)) {
    return data.map((item, index) => (
      <Fragment key={`${prefix}-array-${index}`}>
        {renderKeyValuePairs(
          item,
          localization,
          testIdPrefix,
          prefix + index + ".",
        )}
      </Fragment>
    ));
  }

  if (typeof data !== "object" || data === null) {
    return null;
  }

  const obj = data as Record<string, unknown>;
  return Object.entries(obj)
    .filter(
      ([key, val]) =>
        val !== undefined &&
        key !== "timeline" &&
        !EXCLUDED_RAW_FIELD_PATHS.has(prefix + key),
    )
    .map(([key, val]) => {
      if (val instanceof Date) {
        val = val.toLocaleString(localization.locale, {
          timeZone: localization.timeZone,
        });
      }

      if (typeof val !== "object") {
        const fullKey = prefix + key;
        return (
          <Stack key={`${prefix}-${key}`}>
            <Typography
              variant="body2DashedDivider"
              sx={{ fontWeight: "bold" }}
              data-testid={`${testIdPrefix}-${fullKey}-label-text`}
            >
              {fullKey}
            </Typography>
            <Typography
              variant="body2"
              data-testid={`${testIdPrefix}-${fullKey}-text`}
            >
              {String(val)}
            </Typography>
          </Stack>
        );
      }

      return (
        <Fragment key={prefix + key + "."}>
          {renderKeyValuePairs(
            val,
            localization,
            testIdPrefix,
            prefix + key + ".",
          )}
        </Fragment>
      );
    });
}

export function ExtrasSection({
  transactionData,
  metadata,
  transactionId,
}: ExtrasSectionProps) {
  const { localization } = useConfig();
  const [isRawExpanded, setRawExpanded] = useState(false);
  const [isMetadataExpanded, setMetadataExpanded] = useState(false);

  const isAdminTx = isAdminTransaction(transactionData);

  const { data: metadataData, isFetching: isMetadataFetching } =
    CoreQuery.useAdmin_getF2fTransactionMetadataQuery(transactionId ?? 0, {
      enabled: !!transactionId && isMetadataExpanded && isAdminTx,
    });

  const finalMetadata = metadata ?? metadataData;

  const schemaVersion = isAdminTx ? transactionData.schema_version : undefined;

  return (
    <Stack
      spacing={5}
      sx={{
        p: 3,
        backgroundColor: "primary._states.hover",
        borderRadius: 2,
      }}
    >
      <Typography
        variant="h5SolidDivider"
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <PublicSvgIcon name="add_ad" /> Extras
      </Typography>

      <Stack spacing={2}>
        {schemaVersion !== undefined && (
          <Box>
            <Typography
              variant="body2DashedDivider"
              sx={{ fontWeight: "bold" }}
            >
              Schema Version
            </Typography>
            <Typography
              variant="body2"
              data-testid="extras-schema-version-text"
            >
              {schemaVersion || "N/A"}
            </Typography>
          </Box>
        )}

        <Stack>
          <Accordion
            expanded={isRawExpanded}
            onChange={(_, expanded) => setRawExpanded(expanded)}
          >
            <AccordionSummary
              data-testid="extras-raw-button"
              expandIcon={
                <PublicSvgIcon
                  name={isRawExpanded ? "expand_less" : "expand_more"}
                />
              }
            >
              <Typography variant="body1">Raw Transaction</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2} direction="column">
                {renderKeyValuePairs(
                  transactionData,
                  localization,
                  "extras-raw",
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>

        {(finalMetadata !== undefined || transactionId) && (
          <Stack>
            <Accordion
              expanded={isMetadataExpanded}
              onChange={(_, expanded) => setMetadataExpanded(expanded)}
            >
              <AccordionSummary
                data-testid="extras-metadata-button"
                expandIcon={
                  <PublicSvgIcon
                    name={isMetadataExpanded ? "expand_less" : "expand_more"}
                  />
                }
              >
                <Typography variant="body1">Metadata</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2} direction="column">
                  {!isMetadataFetching &&
                    finalMetadata &&
                    renderKeyValuePairs(
                      finalMetadata,
                      localization,
                      "extras-metadata",
                    )}

                  {!isMetadataFetching && !finalMetadata && (
                    <Typography variant="body2">
                      No metadata available.
                    </Typography>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
